import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, formatApiErrorDetail } from "@/lib/api";
import { FadeUp, Overline } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELD = "h-11 rounded-md border-forest-700/20 bg-white focus-visible:ring-terra";

export default function ContactPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", suburb: "", postcode: "",
    product_interest: params.get("interest") || "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const interest = params.get("interest");
    if (interest) setForm((f) => ({ ...f, product_interest: interest }));
  }, [params]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-28 sm:pt-32" data-testid="contact-page">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-24 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Overline className="text-terra">Request a quote</Overline>
          <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-forest-700 sm:text-5xl" data-testid="contact-title">
            Let's talk about your place.
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-moss">
            Tell us what you're considering — a heat pump, air conditioning,
            solar, batteries, or all four. We'll come back with a tailored
            quote, every rebate you qualify for already priced in.
          </p>
          <ul className="mt-10 space-y-5 text-sm" data-testid="contact-details">
            <li className="flex items-center gap-3.5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-forest-700/10">
                <Mail className="h-4.5 w-4.5 text-forest-700" />
              </span>
              <div>
                <p className="font-bold text-forest-700">Email</p>
                <a href="mailto:info@melwestecoenergy.com.au" data-testid="contact-email" className="text-moss hover:text-terra">info@melwestecoenergy.com.au</a>
              </div>
            </li>
            <li className="flex items-center gap-3.5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-forest-700/10">
                <Phone className="h-4.5 w-4.5 text-forest-700" />
              </span>
              <div>
                <p className="font-bold text-forest-700">Phone</p>
                <a href="tel:1300768360" data-testid="contact-phone" className="text-moss hover:text-terra">1300 768 360</a>
              </div>
            </li>
            <li className="flex items-center gap-3.5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-forest-700/10">
                <MapPin className="h-4.5 w-4.5 text-forest-700" />
              </span>
              <div>
                <p className="font-bold text-forest-700">Service area</p>
                <p className="text-moss">Greater Melbourne &amp; regional Victoria</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <FadeUp>
            {done ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-forest-700/15 bg-white px-8 py-20 text-center" data-testid="quote-success">
                <CheckCircle2 className="h-14 w-14 text-forest-500" />
                <h2 className="font-display mt-5 text-2xl font-extrabold text-forest-700">Request received</h2>
                <p className="mt-2 max-w-sm text-sm text-moss">
                  Thanks {form.name.split(" ")[0]} — we've got your request and
                  we'll be in touch shortly with next steps.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-xl border border-forest-700/15 bg-white p-7 sm:p-9" data-testid="quote-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="q-name">Full name *</label>
                    <Input id="q-name" required minLength={2} className={FIELD} value={form.name} onChange={set("name")} data-testid="quote-form-name" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="q-phone">Phone *</label>
                    <Input id="q-phone" required minLength={6} type="tel" className={FIELD} value={form.phone} onChange={set("phone")} data-testid="quote-form-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="q-email">Email *</label>
                    <Input id="q-email" required type="email" className={FIELD} value={form.email} onChange={set("email")} data-testid="quote-form-email" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="q-suburb">Suburb</label>
                    <Input id="q-suburb" className={FIELD} value={form.suburb} onChange={set("suburb")} data-testid="quote-form-suburb" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="q-postcode">Postcode</label>
                    <Input id="q-postcode" inputMode="numeric" maxLength={4} className={FIELD} value={form.postcode} onChange={set("postcode")} data-testid="quote-form-postcode" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700">What are you interested in?</label>
                    <Select value={form.product_interest} onValueChange={(v) => setForm((f) => ({ ...f, product_interest: v }))}>
                      <SelectTrigger className={`${FIELD} w-full`} data-testid="quote-form-interest">
                        <SelectValue placeholder="Choose a system (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>
                        ))}
                        <SelectItem value="Trade STC partnership">Trade STC partnership</SelectItem>
                        <SelectItem value="Something else">Something else</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="q-message">Anything else?</label>
                    <Textarea id="q-message" rows={4} className="rounded-md border-forest-700/20 bg-white focus-visible:ring-terra" value={form.message} onChange={set("message")} placeholder="Number of occupants, current system, timelines…" data-testid="quote-form-message" />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-form-submit"
                  className="mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-terra px-7 text-sm font-bold text-bone hover:bg-terra-dark sm:w-auto"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {sending ? "Sending…" : "Request my quote"}
                </Button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
